import { useTranslation } from "react-i18next";

import { languageOf, type Locale } from "../i18n/languages";

/**
 * THE LANGUAGE THE LEGAL DOCUMENTS ARE WRITTEN IN — which is not the language of the page
 * around them.
 *
 * Terms of Use and the Privacy Policy are English on all 24 locales by an owner decision
 * (they live in src/i18n/legal/en.json and scripts/prerender.mjs merges that one file into
 * every locale). On Arabic that put English prose inside <html dir="rtl">: the latin text
 * jammed against the right margin and the clause numbering and punctuation reordered, which
 * looked broken because it was.
 *
 * The fix below therefore asks for the DOCUMENT's direction, never the page's. Writing it as
 * a locale rather than as a hardcoded "ltr" is the whole point: the direction is looked up
 * from the language registry, so the day a document is genuinely translated this becomes the
 * page locale and the direction follows on its own.
 *
 * ⚠ TRANSLATING THE DOCUMENTS IS A ONE-LINE CHANGE, HERE. Point this at the page locale
 * (`i18n.language as Locale`) and nothing else in this file moves.
 *
 * Only the <article> is affected. The header, the footer and the language switcher are real
 * translations and stay mirrored — this deliberately does not touch them.
 */
const DOCUMENT_LOCALE: Locale = "en";

export interface LegalProps {
  page: "privacy" | "terms";
}

/** How many numbered clauses the layout is willing to render. Absent ones render nothing. */
const MAX_SECTIONS = 20;

/**
 * Splits one clause body into paragraphs.
 *
 * A legal clause is not one thought — it is a numbered run of sub-clauses (1.1, 1.2, 6.9.1),
 * and printing the whole run as a single <p> turns a document into a wall. Two rules, tried
 * in order:
 *
 *  1. LINE BREAKS, if the text has any. That is the authored, unambiguous separator, and it
 *     is what this function prefers whenever the dictionary supplies it.
 *  2. THE CLAUSE NUMBERING ITSELF, when it does not. ⚠ Today's English legal text contains no
 *     line breaks at all — all 26 clause bodies are single unbroken strings — so rule 1 alone
 *     would render exactly the wall it exists to prevent. The numbering, however, is already
 *     in the text, so the split is *reading* structure the author wrote rather than inventing
 *     any: a break is taken before a "1.2." / "6.9.1." token only where it follows whitespace
 *     and is itself followed by a space, which is the shape of a sub-clause opener and not of
 *     a figure quoted mid-sentence.
 *
 * This is a display-layer heuristic and nothing more — no markup is expected in the JSON, and
 * the day the source gains real line breaks rule 1 takes over silently.
 */
const SUBCLAUSE_BOUNDARY = /\s+(?=\d+(?:\.\d+)+\.\s)/;

export function toParagraphs(body: string): string[] {
  const byLine = body
    .split(/\r?\n+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (byLine.length > 1) return byLine;

  return body
    .split(SUBCLAUSE_BOUNDARY)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * The two legal routes.
 *
 * The shape is driven entirely by keys — `privacy.sections.1.title` / `.body`, and the same
 * under `terms.` — and stops at the first index the dictionary does not define. Publishing or
 * revising the document is therefore a translation change alone: the page grows its clauses,
 * its table of contents and its anchors without a line of layout work here.
 *
 * ⚠ THE DRAFT NOTICE IS STILL ON. The text is not yet approved by a lawyer, so the page says
 * so. Removing it is a ONE-LINE edit: delete the `<p className="legal__draft">…</p>` element
 * below (the `legal.draftNotice` key then becomes unused and can be dropped from the
 * dictionary separately). Nothing else references it.
 */
export function Legal({ page }: LegalProps) {
  const { t, i18n } = useTranslation();

  const sections = Array.from({ length: MAX_SECTIONS }, (_, index) => index + 1)
    .map((index) => ({
      index,
      titleKey: `${page}.sections.${index}.title`,
      bodyKey: `${page}.sections.${index}.body`,
    }))
    .filter((section) => i18n.exists(section.titleKey));

  // `lang` travels with `dir`: a screen reader that is told the direction but not the
  // language reads English clauses with Arabic pronunciation rules.
  const legalDocument = languageOf(DOCUMENT_LOCALE);

  return (
    <article className="legal" lang={legalDocument.code} dir={legalDocument.dir}>
      <header className="legal__head">
        <h1>{t(`${page}.heading`)}</h1>
        {/* The publication and revision dates. On a legal document this is not decoration —
            a policy with no date cannot be told apart from the version it replaced — so it
            sits with the heading rather than somewhere down the page. */}
        {i18n.exists(`${page}.updated`) ? (
          <p className="legal__updated">{t(`${page}.updated`)}</p>
        ) : null}
      </header>

      {/* The one genuinely TRANSLATED string inside the document, so it is the one thing here
          that must not inherit the document's direction: on Arabic it is Arabic prose sitting
          in an ltr <article>. `auto` reads the direction off the text's own first strong
          character, which keeps it right for all 24 locales without a second locale table. */}
      <p className="legal__draft" role="note" dir="auto">
        {t("legal.draftNotice")}
      </p>

      {sections.length > 0 ? (
        <nav className="legal__toc" aria-labelledby="legal-toc-heading">
          <h2 className="legal__toc-heading" id="legal-toc-heading">
            {t(`${page}.heading`)}
          </h2>
          <ol className="legal__toc-list">
            {sections.map((section) => (
              <li key={section.index}>
                <a href={`#clause-${section.index}`}>{t(section.titleKey)}</a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="legal__body">
        {sections.map((section) => (
          <section className="legal__clause" id={`clause-${section.index}`} key={section.index}>
            <h2 className="legal__clause-title">{t(section.titleKey)}</h2>
            {toParagraphs(t(section.bodyKey)).map((paragraph, index) => (
              // The index is a safe key here: the list is derived from one immutable string
              // and is re-derived whole whenever that string changes.
              <p className="legal__clause-body" key={index}>
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>
    </article>
  );
}
